// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Earrings are the easiest antique jewelry to date, because the fitting is a mechanism, mechanisms were patented, and patents have dates. Turn a pair over and the back tells you more in five seconds than the front will in an hour.

## The fittings, roughly in order of age

**Shepherd hook, also called French wire.** A plain curved wire through the ear. The oldest and simplest, in use for centuries and still made today, so on its own it dates nothing. What helps is the wire: hand drawn antique wire is often slightly uneven, and old hooks are frequently thicker than modern ones.

**Kidney wire.** A hook that closes into a kidney shape with a small catch. Nineteenth century into the early twentieth.

**Screw back.** A threaded post with a disc that screws up against the earlobe, for unpierced ears. Introduced around the turn of the twentieth century and dominant from roughly 1900 to the 1940s. A screw back is a strong signal of that window.

**Clip back.** A hinged, spring-loaded clip. Patented in the 1930s and everywhere from then into the 1970s. A clip on a piece described as Victorian means the fitting is later.

**Screw-and-clip combination.** A clip with an adjustable screw, mid twentieth century.

**Post and butterfly.** A straight post through the ear with a friction back. Twentieth century, and increasingly standard from the 1950s onward as pierced ears returned to fashion.

**Lever back.** A hinged wire that latches closed. Nineteenth century onwards, and still made, so it dates loosely. Antique lever backs tend to be heavier and more solidly made.

## What each fitting tells you

| Fitting | Suggests |
| --- | --- |
| Shepherd hook | Any period. Judge the wire, not the shape |
| Kidney wire | 19th to early 20th century |
| Screw back | About 1900 to 1940s |
| Clip back | 1930s onwards |
| Post and butterfly | Mid 20th century onwards |
| Lever back | 19th century onwards, weight helps |

## The trap: converted earrings

Fittings get replaced constantly, and pierced ears going in and out of fashion is why. A Georgian drop earring may well be on a modern hook, because a screw fitting was fitted in 1910 and swapped for a post in 1975.

So the rule is: **the fitting dates the fitting.** Judge the body of the earring separately, on its metal, stone cuts and construction, using the [era by era guide](/blog/how-to-date-antique-jewelry-by-era).

Signs of a converted fitting:

- Solder at the join that is a different colour from the earring body.
- A small scar or filled hole where a previous fitting sat.
- A fitting in a different metal or a different karat from the piece.
- A modern machine-made post soldered to visibly handmade work.

None of these are faults. All of them should be disclosed, and they affect the price.

## Pairs, and whether they started as a pair

Antique earrings are often married: two singles put together because they look alike. Check the two against each other.

- Are the stones the same size, the same cut and the same colour?
- Is the metalwork identical, or merely similar?
- Do both fittings match, in type, metal and wear?
- Do they weigh the same? Ask for both weights.

A married pair is perfectly wearable and much cheaper than a true pair. It is only a problem when it is sold as original.

## Condition, where earrings actually fail

**Screw backs**: threads wear and the disc no longer grips. Repairable, but the part is small and fiddly.

**Clip springs**: lose tension. A jeweller can re-tension or replace a clip, though matching an antique clip exactly is difficult.

**Post joins**: the join between post and earring body takes all the leverage. Look for a hairline crack or a repair there.

**Drops and hinges**: on articulated earrings, the hinge or jump ring wears through. Check for a nearly-closed ring worn to a sliver.

**Ear wire thinning**: an old hook worn thin will eventually break, usually while being worn.

## Screw back to pierced: worth it or not?

A common question, because screw backs are uncomfortable and a lot of lovely 1900 to 1940 earrings have them.

Converting is straightforward work for a jeweller. But it permanently alters an antique, it removes a dating feature, and it lowers value to a collector. If the earrings are ordinary, convert them and enjoy them. If they are fine, signed, or unusual, consider that the discomfort is the price of originality, and look for a piece that was pierced to begin with.

If you do convert, keep the original fittings in a labelled envelope. It costs nothing and it matters if the piece is ever sold.

## Weight, and why it decides comfort

Ask for the weight in grams before you buy drops. Antique gold drop earrings can be surprisingly heavy, and a weight that is fine for an hour is not fine for a day. Under about 3 grams each is comfortable for most people; above 6 grams each you will notice them.

Weight also matters for value, and our [resale guide](/blog/how-much-is-my-jewelry-worth) explains how to turn weight and purity into a floor price.

## What to ask a seller

1. What type of fitting is it, and is it original to the earrings?
2. If it has been changed, what was there before?
3. Do the two match in weight, and can I have both figures?
4. Are the screw threads or clip springs working properly?
5. Can I see a clear photograph of the backs?

That last one does most of the work. A photograph of the backs shows the fitting type, the solder, the wear and often the mark, all at once. Our [guide to spotting reproductions](/blog/how-to-spot-fake-antique-jewelry) covers what else the back gives away.

On Nextmine the buyer's payment is held until the earrings have arrived and been confirmed. [See what is listed](/s), or read [how payments work](/how-payments-work).

The Hallmark Society's [dating method](https://hallmarksociety.com/how-to-date-antique-jewelry/) sets out what each piece of evidence actually proves, which is the discipline this whole subject needs.
`;

export default body;
